import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Autocomplete,
  TextField,
  Dialog,
  DialogTitle,
  IconButton,
  DialogContent,
  Button,
  Chip,
  Grid,
  DialogActions
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { API } from '../../config/config';
import { useAuth } from '../../hooks/useAuth';
import { Perk } from '../../interface/interface';
import { LinkItem } from '../../hooks/useUpload';
import { PrizetS3Upload } from '../PrizetS3Upload';

interface SellerDetailProps {
  currentSeller: any;
  setCurrentSeller: React.Dispatch<React.SetStateAction<any>>;
  fetchData: () => void;
}

const SellerDetailsModal: React.FC<SellerDetailProps> = ({
  currentSeller,
  fetchData,
  setCurrentSeller
}) => {
  const { enhancedFetch } = useAuth();
  const [onS3UploadCallback, setOnS3UploadCallback] = useState<LinkItem[]>([]);
  const [availablePerks, setAvailablePerks] = useState<Perk[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [formFields, setFormFields] = useState({
    fullName: '',
    address: '',
    businessName: '',
    phoneNumber: '',
    city: '',
    perks: [] as Perk[],
    businessLogo: ''
  });

  // Fetch perks when the component mounts
  const fetchPerks = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await enhancedFetch(`${API.HOST}/api/v1/perks`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      setAvailablePerks(response || []);
    } catch (error) {
      console.error('Error fetching perks:', error.message || error);
    }

    // cleanup function here
    return () => {
      setIsLoading(false);
    };
  }, [enhancedFetch]);

  useEffect(() => {
    fetchPerks();
  }, []);

  // Update formFields when currentSeller changes
  useEffect(() => {
    if (currentSeller) {
      setFormFields({
        fullName: currentSeller.name || '',
        address: currentSeller.addressLine1 || '',
        businessName: currentSeller.company || '',
        phoneNumber: currentSeller.phoneNumber || '',
        city: currentSeller.city || '',
        perks: currentSeller.perks || [],
        businessLogo: currentSeller.businessLogo || ''
      });
    }
  }, [currentSeller]);

  useEffect(() => {
    if (onS3UploadCallback) {
      const firstImage = onS3UploadCallback[0];
      setFormFields((prev) => ({
        ...prev,
        businessLogo: firstImage?.image
      }));
    }
  }, [onS3UploadCallback]);

  // Handle input changes for text fields
  const handleInputChange =
    (field: keyof typeof formFields) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value;
      setFormFields((prev) => ({
        ...prev,
        [field]: newValue
      }));

      setCurrentSeller((prev) => ({
        ...prev,
        [field === 'address' ? 'addressLine1' : field]: newValue, //bug fixed
        [field === 'businessName' ? 'company' : field]: newValue, //bug fixed
        [field === 'fullName' ? 'name' : field]: newValue
      }));
    };

  // Handle perk selection changes
  const handlePerkChange = (event: any, newValue: Perk[]) => {
    setFormFields((prev) => ({
      ...prev,
      perks: newValue
    }));

    setCurrentSeller((prev) => ({
      ...prev!,
      perks: newValue
    }));
  };

  // Handle saving changes
  const handleSaveChanges = async () => {
    if (!currentSeller) {
      console.error('currentSeller is null');
      return;
    }

    try {
      const updatedSeller = {
        fullName: formFields.fullName,
        businessName: formFields.businessName,
        phoneNumber: formFields.phoneNumber,
        address: formFields.address,
        city: formFields.city,
        perks: formFields.perks.map((perk) => perk.id), // Send only the perk IDs
        businessLogo: formFields.businessLogo
      };
      await enhancedFetch(
        `${API.HOST}/api/v1/sellers/${currentSeller.id}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(updatedSeller)
        }
      );

      fetchData();
      setCurrentSeller(null);
    } catch (error) {
      console.error('Network error:', error);
    }
  };

  return (
    <Dialog
      open={!!currentSeller}
      maxWidth={'md'}
      onClose={() => setCurrentSeller(null)}
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: '8px',
          padding: '16px'
        }
      }}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        Seller Details
        <IconButton
          onClick={() => setCurrentSeller(null)}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: 'grey.500'
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ mt: 2 }}>
        <Box
          component="form"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 5,
            mt: 1
          }}
        >
          <Grid container style={{ marginTop: '2%', rowGap: '31px'}} spacing={2}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Full Name"
                value={formFields.fullName}
                onChange={handleInputChange('fullName')}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Company"
                value={formFields.businessName}
                onChange={handleInputChange('businessName')}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Address"
                value={formFields.address}
                onChange={handleInputChange('address')}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="City"
                value={formFields.city}
                onChange={handleInputChange('city')}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Phone Number"
                value={formFields.phoneNumber}
                onChange={handleInputChange('phoneNumber')}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={6}>
              <Autocomplete
                multiple
                options={availablePerks}
                value={formFields.perks}
                onChange={handlePerkChange}
                getOptionLabel={(option) => (option ? option.title : '')}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    label="Perks"
                    placeholder="Select perks"
                  />
                )}
                renderTags={(value, getTagProps) => {
                  return value?.map((option, index) => (
                    <Chip
                      label={option?.title}
                      {...getTagProps({ index })}
                      key={option?.id}
                    />
                  ));
                }}
                loading={isLoading}
                isOptionEqualToValue={(option, value) => option.id === value.id}
              />
            </Grid>
            <Grid item xs={6}>
              <PrizetS3Upload
                key={'upload'}
                links={[]}
                onS3UploadCallback={(items) => setOnS3UploadCallback(items)}
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setCurrentSeller(null)}>Cancel</Button>
        <Button variant="contained" onClick={handleSaveChanges}>
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SellerDetailsModal;
